import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Checkbox,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddLinkIcon from "@mui/icons-material/AddLink";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import UndoIcon from "@mui/icons-material/Undo";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import DirectionsBoatOutlinedIcon from "@mui/icons-material/DirectionsBoatOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import SwapVertOutlinedIcon from "@mui/icons-material/SwapVertOutlined";
import AltRouteOutlinedIcon from "@mui/icons-material/AltRouteOutlined";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import {
  updateLogisticaEnvio,
  getLogisticaEnviosResumen,
  deleteVinculoPedidoLogisticaEnvio,
  deleteLogisticaEnvio,
} from "../../api/Logistica/logisticaEnviosApi";
import VincularPedidoLogisticaDialog from "./VincularPedidoLogisticaDialog";
import SeleccionarPedidoNuevoEnvioDialog from "./SeleccionarPedidoNuevoEnvioDialog";
import PedidoEnviosDialog from "../Produccion/PedidoEnviosDialog";
import LogisticaEnvioEditDialog from "./LogisticaEnvioEditDialog";
import EnvioDocumentosChips from "./EnvioDocumentosChips";
import EnvioPedidoNameChip from "./EnvioPedidosChips";
import EnvioSectionBand from "./EnvioSectionBand";
import LogisticaEnviosTree from "./LogisticaEnviosTree";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import { fmtQtyMatriz } from "../Produccion/a3PedidoMatrizUtils";
import {
  fmtLogisticaDate as fmtDate,
  getPedidoClienteLabel,
  getPedidoSupplierLabel,
  getShipmentMethodType,
  groupLogisticaEnviosByCliente,
  matchesSearchTokens,
  pedidoRowFromLogisticaLink,
  presupuestoFromPedidoRow,
  sumUnitsFromEnvioLineas as totalUnitsFromEnvioLineas,
  tokenizeSearch,
} from "./logisticaUtils";

const fmtMetric = (v) => {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? fmtQtyMatriz(n) : "—";
};

const GestionLogisticaPedidos = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [articleFilter, setArticleFilter] = useState("");
  const [textFilter, setTextFilter] = useState("");
  const [showClosed, setShowClosed] = useState(false);
  const [etaSort, setEtaSort] = useState("none"); // none | asc | desc
  const [vincularOpen, setVincularOpen] = useState(false);
  const [vincularEnvioId, setVincularEnvioId] = useState(null);
  const [vincularProductId, setVincularProductId] = useState(null);
  const [vincularExcludedIds, setVincularExcludedIds] = useState([]);
  const [vincularAnchorCodPros, setVincularAnchorCodPros] = useState([]);
  const [editEnvio, setEditEnvio] = useState(null);
  const [unlinkDialog, setUnlinkDialog] = useState(null);
  const [unlinkLoading, setUnlinkLoading] = useState(false);
  const [deleteEnvioRow, setDeleteEnvioRow] = useState(null);
  const [deleteEnvioLoading, setDeleteEnvioLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [selectedEnvioId, setSelectedEnvioId] = useState(null);
  const [nuevoEnvioPickOpen, setNuevoEnvioPickOpen] = useState(false);
  const [enviosDialogPedido, setEnviosDialogPedido] = useState(null);
  const [enviosDialogAutoNuevo, setEnviosDialogAutoNuevo] = useState(false);
  const { contentMargin } = useContext(SidebarContext);
  const filterFieldSx = {
    minWidth: { xs: "100%", sm: 180 },
    "& .MuiInputBase-root": {
      backgroundColor: alpha(theme.palette.background.paper, 0.72),
      backdropFilter: "blur(4px)",
      borderRadius: 2,
      fontSize: "0.86rem",
      minHeight: 38,
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary,
      fontSize: "0.82rem",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
    },
  };
  const searchFieldSx = {
    ...filterFieldSx,
    minWidth: { xs: "100%", sm: 220 },
    flex: { sm: "1 1 260px" },
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getLogisticaEnviosResumen({
        limit: 800,
        includeClosed: showClosed,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [showClosed]);

  useEffect(() => {
    load();
  }, [load]);

  const openEnviosDePedido = useCallback(
    (pedidoRow, { autoNuevo = false } = {}) => {
      if (!pedidoRow?.IdPresupuestoA3Pedido) return;
      setEnviosDialogAutoNuevo(Boolean(autoNuevo));
      setEnviosDialogPedido(pedidoRow);
    },
    [],
  );

  const enviosDialogPresupuesto = useMemo(
    () => presupuestoFromPedidoRow(enviosDialogPedido),
    [enviosDialogPedido],
  );

  const filtered = useMemo(() => {
    const qTokens = tokenizeSearch(textFilter);
    const artTokens = tokenizeSearch(articleFilter);
    const base = rows.filter((e) => {
      const links = Array.isArray(e.pedidoLinks) ? e.pedidoLinks : [];
      const pedidoValues = links.flatMap((lnk) => {
        const p = lnk?.pedidoA3;
        if (!p) return [];
        return [
          p.IdPresupuestoA3Pedido,
          p.Referencia,
          p.CodArt,
          p.IdPresupuesto,
          p.NumDocVenta,
          p.IdPedv,
          p.ClienteNombre,
          p.NomCli,
          p.RazonSocial,
          p.CodCli,
          p.ProveedorNombre,
          p.NomPro,
          p.SupplierName,
          p.CodPro,
          getPedidoClienteLabel(p),
          getPedidoSupplierLabel(p),
        ];
      });

      if (
        artTokens.length &&
        !matchesSearchTokens(
          links.flatMap((lnk) => {
            const p = lnk?.pedidoA3;
            return p ? [p.CodArt, p.Referencia] : [];
          }),
          artTokens,
        )
      ) {
        return false;
      }

      if (!qTokens.length) return true;

      return matchesSearchTokens(
        [
          e.IdLogisticaEnvio,
          e.ProductId,
          e.POReference,
          e.BLAWBNr,
          e.ShipmentCodeCustomer,
          e.ShipmentMethod,
          e.Forwarder,
          e.Factory,
          e.Remarks,
          e.LinkDocs,
          e.IsClosed ? "cerrada archivada closed" : "abierta activa open",
          fmtDate(e.InspectionDate),
          fmtDate(e.ExFD),
          fmtDate(e.ETDF),
          fmtDate(e.CutOff),
          fmtDate(e.ETD),
          fmtDate(e.DD),
          fmtDate(e.ETA),
          fmtDate(e.BookingDone),
          fmtDate(e.DocumentsApproved),
          fmtDate(e.OriginalTelexDocsSemt),
          ...(Array.isArray(e.documentos)
            ? e.documentos.flatMap((d) => [d?.TipoDocumento, d?.Archivo])
            : []),
          ...pedidoValues,
        ],
        qTokens,
      );
    });
    if (etaSort === "none") return base;
    const toEtaMs = (row) => {
      const raw = row?.ETA;
      if (!raw) return Number.POSITIVE_INFINITY;
      const ms = new Date(raw).getTime();
      return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
    };
    const dir = etaSort === "asc" ? 1 : -1;
    return [...base].sort((a, b) => {
      const diff = toEtaMs(a) - toEtaMs(b);
      if (diff === 0)
        return (
          Number(a?.IdLogisticaEnvio || 0) - Number(b?.IdLogisticaEnvio || 0)
        );
      return diff * dir;
    });
  }, [rows, textFilter, articleFilter, etaSort]);

  const clienteGroups = useMemo(
    () =>
      groupLogisticaEnviosByCliente(filtered, {
        consolidado: t("logisticaPedidos.treeConsolidado"),
        sinCliente: t("logisticaPedidos.treeSinCliente"),
        variosArticulos: t("logisticaPedidos.treeVariosArticulos"),
        sinArticulo: t("logisticaPedidos.treeSinArticulo"),
      }),
    [filtered, t],
  );

  useEffect(() => {
    if (!filtered.length) {
      setSelectedEnvioId(null);
      return;
    }
    const stillVisible = filtered.some(
      (row) => Number(row?.IdLogisticaEnvio) === Number(selectedEnvioId),
    );
    if (!stillVisible) {
      setSelectedEnvioId(Number(filtered[0].IdLogisticaEnvio));
    }
  }, [filtered, selectedEnvioId]);

  const selectedEnvio = useMemo(
    () =>
      filtered.find(
        (row) => Number(row?.IdLogisticaEnvio) === Number(selectedEnvioId),
      ) || null,
    [filtered, selectedEnvioId],
  );

  const heroStats = useMemo(() => {
    const totalPedidos = filtered.reduce((acc, row) => {
      const links = Array.isArray(row?.pedidoLinks)
        ? row.pedidoLinks.length
        : 0;
      return acc + links;
    }, 0);
    const abiertas = filtered.filter((row) => !row?.IsClosed).length;
    const cerradas = filtered.filter((row) => Boolean(row?.IsClosed)).length;
    return {
      envios: filtered.length,
      pedidos: totalPedidos,
      abiertas,
      cerradas,
    };
  }, [filtered]);

  const pageShellSx = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100dvh",
    boxSizing: "border-box",
    backgroundColor: theme.palette.background.default,
    ...contentMargin,
  };

  const sectionPxSx = { px: { xs: 1.5, md: 3 } };

  const openVincular = (idLogisticaEnvio, productId, pedidoLinks) => {
    setVincularEnvioId(idLogisticaEnvio);
    setVincularProductId(
      productId != null && productId !== "" ? Number(productId) : null,
    );
    const links = Array.isArray(pedidoLinks) ? pedidoLinks : [];
    const ex = links
      .map(
        (lnk) =>
          lnk?.IdPresupuestoA3Pedido ?? lnk?.pedidoA3?.IdPresupuestoA3Pedido,
      )
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0);
    setVincularExcludedIds(ex);
    const seenCod = new Set();
    const anchorCodes = [];
    for (const l of links) {
      const c = String(l?.pedidoA3?.CodPro ?? "").trim();
      if (c && !seenCod.has(c)) {
        seenCod.add(c);
        anchorCodes.push(c);
      }
    }
    setVincularAnchorCodPros(anchorCodes);
    setVincularOpen(true);
  };

  const handleUnlink = async () => {
    const idEnvio = Number(unlinkDialog?.idEnvio);
    const idPedido = Number(unlinkDialog?.idPedido);
    if (!Number.isFinite(idEnvio) || idEnvio <= 0) return;
    if (!Number.isFinite(idPedido) || idPedido <= 0) return;
    setUnlinkLoading(true);
    setError("");
    try {
      await deleteVinculoPedidoLogisticaEnvio(idEnvio, idPedido);
      setUnlinkDialog(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setUnlinkLoading(false);
    }
  };

  const handleDeleteEnvio = async () => {
    const idEnvio = Number(deleteEnvioRow?.IdLogisticaEnvio);
    if (!Number.isFinite(idEnvio) || idEnvio <= 0) return;
    setDeleteEnvioLoading(true);
    setError("");
    try {
      await deleteLogisticaEnvio(idEnvio);
      if (Number(editEnvio?.IdLogisticaEnvio) === idEnvio) setEditEnvio(null);
      if (Number(vincularEnvioId) === idEnvio) {
        setVincularOpen(false);
        setVincularEnvioId(null);
      }
      setDeleteEnvioRow(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setDeleteEnvioLoading(false);
    }
  };

  return (
    <Box sx={pageShellSx}>
      <Box
        sx={{
          ...sectionPxSx,
          pt: { xs: 1, md: 1.25 },
          pb: { xs: 0.9, md: 1 },
          position: "sticky",
          top: 0,
          zIndex: 9,
          backgroundColor: alpha(theme.palette.background.default, 0.9),
          backdropFilter: "blur(8px)",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              theme.palette.mode === "dark" ? 0.18 : 0.08,
            )} 0%, ${alpha(theme.palette.info.main, 0.06)} 60%, ${alpha(
              theme.palette.background.paper,
              0.95,
            )} 100%)`,
            boxShadow: `0 4px 16px ${alpha(
              theme.palette.common.black,
              theme.palette.mode === "dark" ? 0.4 : 0.06,
            )}`,
            p: { xs: 1.1, md: 1.35 },
          }}
        >
          <Stack
            direction={{ xs: "column", xl: "row" }}
            spacing={{ xs: 1, xl: 1.5 }}
            alignItems={{ xs: "stretch", lg: "center" }}
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing={1.1}
              alignItems="flex-start"
              sx={{ minWidth: 0, flexShrink: 0 }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: theme.palette.primary.contrastText,
                  boxShadow: `0 6px 16px ${alpha(
                    theme.palette.primary.main,
                    0.45,
                  )}`,
                  flexShrink: 0,
                }}
              >
                <LocalShippingOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{ flexWrap: "wrap", rowGap: 0.5 }}
                >
                  <Chip
                    label="CRM"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      backgroundColor: alpha(theme.palette.primary.main, 0.12),
                      color: theme.palette.primary.main,
                      border: "1px solid",
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    }}
                  />
                  <Chip
                    label={`${heroStats.envios} envíos`}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      backgroundColor: alpha(theme.palette.info.main, 0.12),
                      color: theme.palette.info.main,
                      border: "1px solid",
                      borderColor: alpha(theme.palette.info.main, 0.3),
                    }}
                  />
                  <Chip
                    label={`${heroStats.pedidos} pedidos`}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      backgroundColor: alpha(
                        theme.palette.secondary.main,
                        0.12,
                      ),
                      color: theme.palette.secondary.main,
                      border: "1px solid",
                      borderColor: alpha(theme.palette.secondary.main, 0.28),
                    }}
                  />
                  <Chip
                    label={`${heroStats.abiertas} abiertas`}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      backgroundColor: alpha(theme.palette.success.main, 0.12),
                      color: theme.palette.success.main,
                      border: "1px solid",
                      borderColor: alpha(theme.palette.success.main, 0.28),
                    }}
                  />
                  {heroStats.cerradas > 0 ? (
                    <Chip
                      label={`${heroStats.cerradas} cerradas`}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        backgroundColor: alpha(
                          theme.palette.text.primary,
                          0.06,
                        ),
                        color: theme.palette.text.secondary,
                      }}
                    />
                  ) : null}
                </Stack>
                <Typography
                  variant="h6"
                  sx={{
                    mt: 0.45,
                    mb: 0.15,
                    fontWeight: 800,
                    lineHeight: 1.15,
                    letterSpacing: -0.2,
                    fontSize: { xs: "1.18rem", md: "1.35rem" },
                  }}
                >
                  {t("logisticaPedidos.title")}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    lineHeight: 1.3,
                    maxWidth: 520,
                    fontSize: "0.84rem",
                  }}
                >
                  Panel de consolidación de envíos, vínculos y estado logístico.
                </Typography>
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
              sx={{
                flex: 1,
                minWidth: 0,
                justifyContent: { xl: "flex-end" },
                rowGap: 0.75,
              }}
            >
              <TextField
                label="Buscar artículo (cod/ref)"
                size="small"
                value={articleFilter}
                onChange={(e) => setArticleFilter(e.target.value)}
                placeholder="cod art o referencia"
                sx={filterFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Inventory2OutlinedIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label={t("logisticaPedidos.filterText")}
                size="small"
                value={textFilter}
                onChange={(e) => setTextFilter(e.target.value)}
                placeholder="envío, pedido, cliente, supplier, forwarder..."
                sx={searchFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setNuevoEnvioPickOpen(true)}
                sx={{
                  borderRadius: 999,
                  minHeight: 38,
                  px: 1.35,
                  fontSize: "0.82rem",
                }}
              >
                {t("logisticaPedidos.nuevoEnvio")}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => load()}
                disabled={loading}
                sx={{
                  borderRadius: 999,
                  minHeight: 38,
                  px: 1.25,
                  backgroundColor: alpha(theme.palette.background.paper, 0.7),
                  fontSize: "0.82rem",
                }}
              >
                {t("logisticaPedidos.refresh")}
              </Button>
              <Button
                variant="outlined"
                startIcon={<HelpOutlineOutlinedIcon />}
                onClick={() => setHelpOpen(true)}
                sx={{
                  borderRadius: 999,
                  minHeight: 38,
                  px: 1.25,
                  backgroundColor: alpha(theme.palette.background.paper, 0.7),
                  fontSize: "0.82rem",
                }}
              >
                Modo de uso
              </Button>
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={0.75}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            sx={{ mt: 0.9 }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
              sx={{ rowGap: 0.5 }}
            >
              {[
                {
                  color: theme.palette.success.main,
                  label: "Abierta / activa",
                },
                {
                  color: theme.palette.text.secondary,
                  label: "Cerrada / archivada",
                },
                {
                  color: theme.palette.info.main,
                  label: "Vínculos de pedidos",
                },
              ].map((item) => (
                <Stack
                  key={item.label}
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: item.color,
                      boxShadow: `0 0 0 2px ${alpha(item.color, 0.2)}`,
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.66rem", fontWeight: 600 }}
                  >
                    {item.label}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showClosed}
                  onChange={(e) => setShowClosed(e.target.checked)}
                />
              }
              label="Mostrar cerradas"
              sx={{
                m: 0,
                px: 1,
                py: 0.1,
                borderRadius: 999,
                border: "1px solid",
                borderColor: alpha(theme.palette.warning.main, 0.28),
                backgroundColor: alpha(theme.palette.warning.main, 0.08),
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  color: theme.palette.text.secondary,
                },
                "& .MuiCheckbox-root": {
                  p: 0.5,
                },
              }}
            />
          </Stack>
        </Box>
      </Box>

      {error ? (
        <Box sx={{ ...sectionPxSx, pt: 1.5 }}>
          <Alert
            severity="error"
            sx={{ borderRadius: 2.5 }}
            onClose={() => setError("")}
          >
            {error}
          </Alert>
        </Box>
      ) : null}

      <Box
        sx={{
          ...sectionPxSx,
          pt: 2,
          pb: 3,
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            mb: 1.5,
            p: 1.25,
            borderRadius: 2.5,
            borderColor: alpha(theme.palette.primary.main, 0.14),
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: `0 6px 18px ${alpha(
              theme.palette.common.black,
              theme.palette.mode === "dark" ? 0.26 : 0.05,
            )}`,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
            >
              <Chip
                size="small"
                label={`${filtered.length} visibles`}
                sx={{
                  fontWeight: 700,
                  backgroundColor: alpha(theme.palette.info.main, 0.12),
                  color: theme.palette.info.main,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.info.main, 0.28),
                }}
              />
              <Chip
                size="small"
                label={
                  etaSort === "none"
                    ? "ETA sin ordenar"
                    : etaSort === "asc"
                      ? "ETA ascendente"
                      : "ETA descendente"
                }
                sx={{
                  fontWeight: 700,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                }}
              />
            </Stack>
            <Button
              size="small"
              variant="outlined"
              startIcon={<SwapVertOutlinedIcon />}
              onClick={() =>
                setEtaSort((prev) =>
                  prev === "none" ? "asc" : prev === "asc" ? "desc" : "none",
                )
              }
              sx={{
                borderRadius: 999,
                alignSelf: { xs: "flex-start", md: "center" },
              }}
            >
              Ordenar ETA
            </Button>
          </Stack>
        </Paper>

        {loading && !filtered.length ? (
          <Paper
            variant="outlined"
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 3,
              borderStyle: "dashed",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">
              {t("logisticaPedidos.loading")}
            </Typography>
          </Paper>
        ) : null}

        {!loading && filtered.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 3,
              borderStyle: "dashed",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">
              {t("logisticaPedidos.empty")}
            </Typography>
          </Paper>
        ) : null}

        {filtered.length ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(240px, 300px) minmax(0, 1fr)",
              },
              gap: 1.25,
              alignItems: "stretch",
            }}
          >
            <LogisticaEnviosTree
              groups={clienteGroups}
              selectedEnvioId={selectedEnvioId}
              onSelectEnvio={setSelectedEnvioId}
            />
            <Box sx={{ minWidth: 0, minHeight: 0, overflow: "auto" }}>
              <Stack spacing={1.25}>
                {(selectedEnvio ? [selectedEnvio] : []).map((e) => {
                  const links = Array.isArray(e.pedidoLinks)
                    ? e.pedidoLinks
                    : [];
                  const pedidoCabeceras = Array.isArray(e.pedidoCabeceras)
                    ? e.pedidoCabeceras
                    : [];
                  const poVenta = [
                    ...new Set(
                      links
                        .map((lnk) => lnk?.pedidoA3)
                        .map((p) => {
                          const num = p?.NumDocVenta ?? p?.IdPedv;
                          return num != null && String(num).trim()
                            ? String(num).trim()
                            : "";
                        })
                        .filter(Boolean),
                    ),
                  ];
                  const articulos = [
                    ...new Set(
                      links
                        .map((lnk) =>
                          String(lnk?.pedidoA3?.CodArt ?? "").trim(),
                        )
                        .filter(Boolean),
                    ),
                  ];
                  const refs = [
                    ...new Set(
                      links
                        .map((lnk) =>
                          String(lnk?.pedidoA3?.Referencia ?? "").trim(),
                        )
                        .filter(Boolean),
                    ),
                  ];
                  const clientes = [
                    ...new Set(
                      links
                        .map((lnk) => getPedidoClienteLabel(lnk?.pedidoA3))
                        .filter(Boolean),
                    ),
                  ];
                  const hasMixedClientes = clientes.length > 1;
                  const clienteSummary = clientes.length
                    ? hasMixedClientes
                      ? `${clientes[0]} + ${clientes.length - 1}`
                      : clientes[0]
                    : String(e.ShipmentCodeCustomer || "").trim() || "—";
                  const suppliers = [
                    ...new Set(
                      links
                        .map((lnk) => getPedidoSupplierLabel(lnk?.pedidoA3))
                        .filter(Boolean),
                    ),
                  ];
                  const hasMixedSuppliers = suppliers.length > 1;
                  const supplierSummary = suppliers.length
                    ? hasMixedSuppliers
                      ? `${suppliers[0]} + ${suppliers.length - 1}`
                      : suppliers[0]
                    : "—";
                  const envioUnitsTotal = totalUnitsFromEnvioLineas(e.lineas);
                  const units =
                    envioUnitsTotal != null
                      ? fmtQtyMatriz(envioUnitsTotal)
                      : "—";
                  const statusColor = e.IsClosed
                    ? theme.palette.text.secondary
                    : theme.palette.success.main;
                  const shipmentMethodType = getShipmentMethodType(
                    e.ShipmentMethod,
                  );
                  const shipmentMethodMeta =
                    shipmentMethodType === "air"
                      ? {
                          label: e.ShipmentMethod || "Aéreo",
                          color: "#0ea5e9",
                          icon: (
                            <FlightTakeoffOutlinedIcon sx={{ fontSize: 15 }} />
                          ),
                        }
                      : shipmentMethodType === "sea"
                        ? {
                            label: e.ShipmentMethod || "Marítimo",
                            color: "#2563eb",
                            icon: (
                              <DirectionsBoatOutlinedIcon
                                sx={{ fontSize: 15 }}
                              />
                            ),
                          }
                        : shipmentMethodType === "seaAir"
                          ? {
                              label: e.ShipmentMethod || "Sea-Air",
                              color: "#14b8a6",
                              icon: (
                                <AltRouteOutlinedIcon sx={{ fontSize: 15 }} />
                              ),
                            }
                          : shipmentMethodType === "combined"
                            ? {
                                label: e.ShipmentMethod || "Combinado",
                                color: "#8b5cf6",
                                icon: (
                                  <AltRouteOutlinedIcon sx={{ fontSize: 15 }} />
                                ),
                              }
                            : shipmentMethodType === "courier"
                              ? {
                                  label: e.ShipmentMethod || "Courier",
                                  color: "#0f766e",
                                  icon: (
                                    <Inventory2OutlinedIcon
                                      sx={{ fontSize: 15 }}
                                    />
                                  ),
                                }
                              : {
                                  label: e.ShipmentMethod || "Terrestre",
                                  color: "#ea580c",
                                  icon: (
                                    <LocalShippingOutlinedIcon
                                      sx={{ fontSize: 15 }}
                                    />
                                  ),
                                };
                  const dateCards = [
                    {
                      label: "Inspection",
                      value: fmtDate(e.InspectionDate),
                      color: theme.palette.error.main,
                    },
                    {
                      label: "ExFD",
                      value: fmtDate(e.ExFD),
                      color: theme.palette.secondary.main,
                    },
                    {
                      label: "ETDF",
                      value: fmtDate(e.ETDF),
                      color: theme.palette.secondary.dark,
                    },
                    {
                      label: "CUT-OFF",
                      value: fmtDate(e.CutOff),
                      color: theme.palette.warning.dark,
                    },
                    {
                      label: "ETD",
                      value: fmtDate(e.ETD),
                      color: theme.palette.primary.dark,
                    },
                    {
                      label: "ETA",
                      value: fmtDate(e.ETA),
                      color: theme.palette.primary.main,
                    },
                    {
                      label: "DD",
                      value: fmtDate(e.DD),
                      color: theme.palette.success.dark,
                    },
                    {
                      label: "Booking",
                      value: fmtDate(e.BookingDone),
                      color: theme.palette.info.main,
                    },
                    {
                      label: "Docs OK",
                      value: fmtDate(e.DocumentsApproved),
                      color: theme.palette.success.main,
                    },
                    {
                      label: "Docs sent",
                      value: fmtDate(e.OriginalTelexDocsSemt),
                      color: theme.palette.warning.main,
                    },
                    {
                      label: "Uds ref.",
                      value: units,
                      color: theme.palette.secondary.main,
                    },
                    {
                      label: "Boxes",
                      value: e.NCartons ?? "—",
                      color: theme.palette.info.main,
                    },
                    {
                      label: "Gross kg",
                      value: e.GrossWeightKgs ?? "—",
                      color: theme.palette.error.main,
                    },
                    {
                      label: "m3",
                      value: e.TotalVolumeCbm ?? "—",
                      color: theme.palette.success.main,
                    },
                  ];
                  const milestoneCards = dateCards.slice(0, 9);
                  const logisticsMetricCards = dateCards.slice(9);

                  return (
                    <Paper
                      key={e.IdLogisticaEnvio}
                      variant="outlined"
                      sx={{
                        borderRadius: 2.5,
                        overflow: "hidden",
                        borderColor: alpha(statusColor, 0.22),
                        backgroundColor: theme.palette.background.paper,
                        transition:
                          "border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 2px 10px rgba(0,0,0,0.35)"
                            : "0 1px 4px rgba(0,0,0,0.05)",
                        "&:hover": {
                          borderColor: alpha(theme.palette.primary.main, 0.4),
                          boxShadow: `0 8px 22px ${alpha(
                            theme.palette.common.black,
                            theme.palette.mode === "dark" ? 0.4 : 0.08,
                          )}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          px: { xs: 1.75, md: 2.25 },
                          py: { xs: 1.5, md: 1.75 },
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md: "minmax(250px, 1.2fr) minmax(250px, 1fr) auto",
                            xl: "minmax(270px, 1.22fr) minmax(290px, 1fr) auto",
                          },
                          gap: { xs: 1.5, md: 2, xl: 2.25 },
                          alignItems: "start",
                          background: `linear-gradient(135deg, ${alpha(
                            statusColor,
                            theme.palette.mode === "dark" ? 0.11 : 0.05,
                          )} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              backgroundColor: statusColor,
                              boxShadow: `0 0 0 3px ${alpha(statusColor, 0.18)}`,
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="center"
                              useFlexGap
                              flexWrap="wrap"
                              sx={{ mb: 0.85, rowGap: 0.85 }}
                            >
                              <Tooltip title={shipmentMethodMeta.label}>
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: alpha(
                                      shipmentMethodMeta.color,
                                      0.12,
                                    ),
                                    color: shipmentMethodMeta.color,
                                    border: "1px solid",
                                    borderColor: alpha(
                                      shipmentMethodMeta.color,
                                      0.24,
                                    ),
                                    flexShrink: 0,
                                  }}
                                >
                                  {shipmentMethodMeta.icon}
                                </Box>
                              </Tooltip>
                              <Chip
                                size="small"
                                label={`#${e.IdLogisticaEnvio}`}
                                sx={{
                                  fontWeight: 800,
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.12,
                                  ),
                                  color: theme.palette.primary.main,
                                  border: "1px solid",
                                  borderColor: alpha(
                                    theme.palette.primary.main,
                                    0.22,
                                  ),
                                }}
                              />
                              <Chip
                                size="small"
                                label={e.IsClosed ? "Cerrada" : "Abierta"}
                                sx={{
                                  fontWeight: 700,
                                  backgroundColor: alpha(statusColor, 0.12),
                                  color: statusColor,
                                  border: "1px solid",
                                  borderColor: alpha(statusColor, 0.28),
                                }}
                              />
                            </Stack>
                            <Typography
                              sx={{
                                fontWeight: 800,
                                fontSize: "0.98rem",
                                lineHeight: 1.2,
                                color: "text.primary",
                              }}
                              noWrap
                            >
                              {poVenta.length
                                ? poVenta.join(" · ")
                                : "Sin PO de venta"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "block",
                                mt: 0.35,
                                lineHeight: 1.35,
                                fontSize: "0.79rem",
                              }}
                              noWrap
                            >
                              {articulos.length
                                ? articulos.join(" · ")
                                : "Sin artículo"}
                            </Typography>
                          </Box>
                        </Stack>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              md: "minmax(165px, 185px) minmax(0, 1fr)",
                              xl: "minmax(175px, 195px) minmax(0, 1fr)",
                            },
                            gap: { xs: 0.9, md: 1.15 },
                            alignItems: "start",
                            minWidth: 0,
                            maxWidth: { md: 430, xl: 500 },
                          }}
                        >
                          {/* BLOQUE CLIENTE / SUPPLIER */}
                          <Stack
                            spacing={0.5}
                            sx={{
                              minWidth: 0,
                              maxWidth: "100%",
                              pr: { md: 1.1, xl: 1.25 },
                              mr: { md: 0.1, xl: 0.25 },
                              borderRight: {
                                md: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                              },
                            }}
                          >
                            <Chip
                              size="small"
                              label={`Cli · ${clienteSummary}`}
                              sx={{
                                maxWidth: "100%",
                                width: "100%",
                                justifyContent: "flex-start",
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                backgroundColor: alpha(
                                  theme.palette.secondary.main,
                                  0.1,
                                ),
                                color: theme.palette.secondary.main,
                                border: "1px solid",
                                borderColor: alpha(
                                  theme.palette.secondary.main,
                                  0.2,
                                ),
                                "& .MuiChip-label": {
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "100%",
                                  textAlign: "left",
                                },
                              }}
                            />
                            <Chip
                              size="small"
                              label={`Sup · ${supplierSummary}`}
                              sx={{
                                maxWidth: "100%",
                                width: "100%",
                                justifyContent: "flex-start",
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                backgroundColor: alpha(
                                  theme.palette.warning.main,
                                  0.1,
                                ),
                                color: theme.palette.warning.main,
                                border: "1px solid",
                                borderColor: alpha(
                                  theme.palette.warning.main,
                                  0.22,
                                ),
                                "& .MuiChip-label": {
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "100%",
                                  textAlign: "left",
                                },
                              }}
                            />
                          </Stack>
                          {/* BLOQUE FORWARDER / REFERENCE */}
                          <Box sx={{ minWidth: 0, pl: { md: 0.15, xl: 0.3 } }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.7rem",
                                letterSpacing: 0.32,
                                textTransform: "uppercase",
                                color: "text.secondary",
                                fontWeight: 700,
                              }}
                            >
                              Forwarder / Reference
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.92rem",
                                lineHeight: 1.2,
                                color: "text.primary",
                              }}
                              noWrap
                            >
                              {e.Forwarder || "—"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "block",
                                mt: 0.1,
                                lineHeight: 1.15,
                                fontSize: "0.78rem",
                              }}
                              noWrap
                            >
                              {hasMixedClientes
                                ? `${clientes.length} clientes en este envío`
                                : hasMixedSuppliers
                                  ? `${suppliers.length} suppliers en este envío`
                                  : refs.length
                                    ? refs.join(" · ")
                                    : "Sin reference"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "block",
                                mt: 0.1,
                                lineHeight: 1.15,
                                fontSize: "0.78rem",
                              }}
                              noWrap
                            >
                              BL/AWB · {e.BLAWBNr || "—"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "block",
                                mt: 0.1,
                                lineHeight: 1.15,
                                fontSize: "0.78rem",
                              }}
                              noWrap
                            >
                              Factory · {e.Factory || "—"}
                            </Typography>
                          </Box>
                        </Box>
                        {/* BOTONES */}
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                          alignItems="center"
                          sx={{
                            justifySelf: { xs: "start", md: "end" },
                            alignSelf: "start",
                            flexShrink: 0,
                          }}
                        >
                          <Tooltip title={t("logisticaPedidos.editEnvio")}>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={(event) => {
                                event.stopPropagation();
                                setEditEnvio(e);
                              }}
                              sx={{
                                border: "1px solid",
                                borderColor: "secondary.main",
                                backgroundColor: alpha(
                                  theme.palette.secondary.main,
                                  0.08,
                                ),
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("logisticaPedidos.linkPedido")}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(event) => {
                                event.stopPropagation();
                                openVincular(
                                  e.IdLogisticaEnvio,
                                  e.ProductId,
                                  e.pedidoLinks,
                                );
                              }}
                              sx={{
                                border: "1px solid",
                                borderColor: "primary.main",
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.08,
                                ),
                              }}
                            >
                              <AddLinkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar envío">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(event) => {
                                event.stopPropagation();
                                setDeleteEnvioRow(e);
                              }}
                              sx={{
                                border: "1px solid",
                                borderColor: "error.main",
                                backgroundColor: alpha(
                                  theme.palette.error.main,
                                  0.08,
                                ),
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              e.IsClosed
                                ? "Reabrir logística"
                                : "Cerrar logística"
                            }
                          >
                            <IconButton
                              size="small"
                              color={e.IsClosed ? "warning" : "success"}
                              onClick={async (event) => {
                                event.stopPropagation();
                                try {
                                  await updateLogisticaEnvio(
                                    e.IdLogisticaEnvio,
                                    {
                                      IsClosed: !Boolean(e.IsClosed),
                                      ClosedAt: !Boolean(e.IsClosed)
                                        ? new Date().toISOString().slice(0, 10)
                                        : null,
                                    },
                                  );
                                  await load();
                                } catch (err) {
                                  setError(
                                    err?.response?.data?.error || err.message,
                                  );
                                }
                              }}
                              sx={{
                                border: "1px solid",
                                borderColor: e.IsClosed
                                  ? "warning.main"
                                  : "success.main",
                                backgroundColor: alpha(
                                  e.IsClosed
                                    ? theme.palette.warning.main
                                    : theme.palette.success.main,
                                  0.08,
                                ),
                              }}
                            >
                              {e.IsClosed ? (
                                <UndoIcon fontSize="small" />
                              ) : (
                                <DoneAllIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        {/* documentos a ancho completo */}
                        <Box
                          sx={{
                            gridColumn: "1 / -1",
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.25,
                            pt: 1.5,
                            mt: 0.25,
                            borderTop: "1px solid",
                            borderColor: "divider",
                            width: "100%",
                            minWidth: 0,
                          }}
                        >
                          <EnvioDocumentosChips
                            documentos={e.documentos}
                            onMissingClick={() => setEditEnvio(e)}
                          />
                        </Box>
                        {/* banda de fechas / metricas */}
                        <Box
                          sx={{
                            gridColumn: "1 / -1",
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              lg: "minmax(0, 1fr) auto",
                            },
                            gap: { xs: 1.25, md: 1.5 },
                            pt: { xs: 1.25, md: 1.5 },
                          }}
                        >
                          <Box
                            sx={{
                              minWidth: 0,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mb: 0.55,
                                color: "text.secondary",
                                fontWeight: 800,
                                letterSpacing: 0.35,
                                textTransform: "uppercase",
                              }}
                            >
                              Fechas clave
                            </Typography>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                  xs: "repeat(2, minmax(88px, 1fr))",
                                  sm: "repeat(3, minmax(88px, 1fr))",
                                  md: "repeat(4, minmax(88px, 1fr))",
                                  xl: "repeat(9, minmax(78px, 1fr))",
                                },
                                gap: 0.6,
                              }}
                            >
                              {milestoneCards.map((item) => (
                                <Box
                                  key={`${e.IdLogisticaEnvio}-${item.label}`}
                                  sx={{
                                    borderRadius: 1.35,
                                    border: "1px solid",
                                    borderColor: alpha(item.color, 0.18),
                                    backgroundColor: alpha(
                                      item.color,
                                      theme.palette.mode === "dark"
                                        ? 0.1
                                        : 0.05,
                                    ),
                                    px: 0.8,
                                    py: 0.58,
                                    minHeight: 48,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    textAlign: "center",
                                    minWidth: 0,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "0.61rem",
                                      letterSpacing: 0.32,
                                      textTransform: "uppercase",
                                      color: "text.secondary",
                                      fontWeight: 700,
                                      lineHeight: 1.08,
                                    }}
                                  >
                                    {item.label}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      mt: 0.22,
                                      fontSize: "0.8rem",
                                      fontWeight: 800,
                                      color: item.color,
                                      lineHeight: 1.12,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {item.value}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              minWidth: 0,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mb: 0.55,
                                color: "text.secondary",
                                fontWeight: 800,
                                letterSpacing: 0.35,
                                textTransform: "uppercase",
                              }}
                            >
                              Magnitudes
                            </Typography>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns:
                                  "repeat(4, minmax(78px, 88px))",
                                gap: 0.6,
                                width: "fit-content",
                              }}
                            >
                              {logisticsMetricCards.map((item) => (
                                <Box
                                  key={`${e.IdLogisticaEnvio}-${item.label}`}
                                  sx={{
                                    borderRadius: 1.45,
                                    border: "1px solid",
                                    borderColor: alpha(item.color, 0.2),
                                    backgroundColor: alpha(
                                      item.color,
                                      theme.palette.mode === "dark"
                                        ? 0.12
                                        : 0.06,
                                    ),
                                    px: 0.85,
                                    py: 0.62,
                                    minHeight: 50,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    textAlign: "center",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "0.62rem",
                                      letterSpacing: 0.34,
                                      textTransform: "uppercase",
                                      color: "text.secondary",
                                      fontWeight: 700,
                                      lineHeight: 1.08,
                                    }}
                                  >
                                    {item.label}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      mt: 0.24,
                                      fontSize: "0.82rem",
                                      fontWeight: 800,
                                      color: item.color,
                                      lineHeight: 1.14,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {item.value}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                      <Divider />
                      <Box sx={{ px: { xs: 1.5, md: 2 }, py: 1.25 }}>
                        <EnvioSectionBand
                          title={t("logisticaPedidos.pedidoMagnitudes")}
                          count={links.length}
                          tone="pedidos"
                        >
                          <Stack spacing={0.9}>
                            {links.length === 0 ? (
                              <Typography
                                variant="caption"
                                color="warning.main"
                              >
                                {t("logisticaPedidos.sinPedidos")}
                              </Typography>
                            ) : (
                              links.map((lnk) => {
                                const p = lnk?.pedidoA3;
                                const idPed =
                                  lnk?.IdPresupuestoA3Pedido ??
                                  p?.IdPresupuestoA3Pedido;
                                const pedidoCliente = getPedidoClienteLabel(p);
                                const pedidoSupplier =
                                  getPedidoSupplierLabel(p);
                                const pedidoMeta = [
                                  hasMixedClientes && pedidoCliente
                                    ? `Cli ${pedidoCliente}`
                                    : "",
                                  hasMixedSuppliers && pedidoSupplier
                                    ? `Sup ${pedidoSupplier}`
                                    : "",
                                ].filter(Boolean);
                                const pedidoCabecera =
                                  pedidoCabeceras.find(
                                    (cab) =>
                                      Number(cab?.IdPresupuestoA3Pedido) ===
                                      Number(idPed),
                                  ) || null;
                                const pedidoUnitsEnvioRaw =
                                  Number(
                                    pedidoCabecera?.Unidades ??
                                      pedidoCabecera?.unidades,
                                  ) || 0;
                                const udsRef =
                                  pedidoUnitsEnvioRaw > 0
                                    ? pedidoUnitsEnvioRaw
                                    : links.length === 1 &&
                                        envioUnitsTotal != null
                                      ? envioUnitsTotal
                                      : null;
                                const pedidoMetrics = [
                                  {
                                    label: "Uds",
                                    value:
                                      udsRef != null
                                        ? fmtQtyMatriz(udsRef)
                                        : "—",
                                    color: theme.palette.secondary.main,
                                  },
                                  {
                                    label: "Boxes",
                                    value: fmtMetric(pedidoCabecera?.NCartons),
                                    color: theme.palette.info.main,
                                  },
                                  {
                                    label: "Kg",
                                    value: fmtMetric(
                                      pedidoCabecera?.GrossWeightKgs,
                                    ),
                                    color: theme.palette.error.main,
                                  },
                                  {
                                    label: "m3",
                                    value: fmtMetric(
                                      pedidoCabecera?.TotalVolumeCbm,
                                    ),
                                    color: theme.palette.success.main,
                                  },
                                ];
                                return (
                                  <Box
                                    key={
                                      lnk.Id ?? `${e.IdLogisticaEnvio}-${idPed}`
                                    }
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: {
                                        xs: "1fr",
                                        md: "minmax(0, 1fr) auto",
                                      },
                                      gap: { xs: 0.55, md: 1 },
                                      alignItems: "center",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        gridColumn: {
                                          xs: "1 / -1",
                                          md: "1 / 2",
                                        },
                                        minWidth: 0,
                                      }}
                                    >
                                      <EnvioPedidoNameChip
                                        lnk={lnk}
                                        hasMixedClientes={hasMixedClientes}
                                        hasMixedSuppliers={hasMixedSuppliers}
                                        onOpenEnvios={(link) =>
                                          openEnviosDePedido(
                                            pedidoRowFromLogisticaLink(link),
                                          )
                                        }
                                        onUnlink={({ idPedido, label }) =>
                                          setUnlinkDialog({
                                            idEnvio: e.IdLogisticaEnvio,
                                            idPedido,
                                            label,
                                          })
                                        }
                                      />
                                      {pedidoMeta.length ? (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            display: "block",
                                            mt: 0.35,
                                            lineHeight: 1.2,
                                            fontSize: "0.78rem",
                                          }}
                                        >
                                          {pedidoMeta.join(" · ")}
                                        </Typography>
                                      ) : null}
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "grid",
                                        gridTemplateColumns:
                                          "repeat(4, minmax(78px, 88px))",
                                        gap: 0.6,
                                        gridColumn: {
                                          xs: "1 / -1",
                                          md: "2 / 3",
                                        },
                                        justifySelf: {
                                          xs: "start",
                                          md: "end",
                                        },
                                        width: "fit-content",
                                      }}
                                    >
                                      {pedidoMetrics.map((item) => (
                                        <Box
                                          key={`${idPed}-${item.label}`}
                                          sx={{
                                            borderRadius: 1.2,
                                            border: "1px solid",
                                            borderColor: alpha(
                                              item.color,
                                              0.18,
                                            ),
                                            backgroundColor: alpha(
                                              item.color,
                                              theme.palette.mode === "dark"
                                                ? 0.12
                                                : 0.06,
                                            ),
                                            px: 0.82,
                                            py: 0.58,
                                            minHeight: 48,
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            textAlign: "center",
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: "0.62rem",
                                              letterSpacing: 0.28,
                                              textTransform: "uppercase",
                                              color: "text.secondary",
                                              fontWeight: 700,
                                              lineHeight: 1.08,
                                            }}
                                          >
                                            {item.label}
                                          </Typography>
                                          <Typography
                                            sx={{
                                              mt: 0.22,
                                              fontSize: "0.82rem",
                                              fontWeight: 800,
                                              color: item.color,
                                              lineHeight: 1.12,
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {item.value}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                );
                              })
                            )}
                          </Stack>
                        </EnvioSectionBand>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        ) : null}

        <SeleccionarPedidoNuevoEnvioDialog
          open={nuevoEnvioPickOpen}
          onClose={() => setNuevoEnvioPickOpen(false)}
          onSelect={(pedidoRow) =>
            openEnviosDePedido(pedidoRow, { autoNuevo: true })
          }
        />
        <PedidoEnviosDialog
          key={enviosDialogPedido?.IdPresupuestoA3Pedido || "envios"}
          open={Boolean(enviosDialogPedido)}
          onClose={() => {
            setEnviosDialogPedido(null);
            setEnviosDialogAutoNuevo(false);
          }}
          productId={enviosDialogPresupuesto?.ProductId || null}
          pedidoRow={enviosDialogPedido}
          presupuesto={enviosDialogPresupuesto}
          autoOpenNuevo={enviosDialogAutoNuevo}
          onChanged={load}
        />
        <VincularPedidoLogisticaDialog
          open={vincularOpen}
          onClose={() => setVincularOpen(false)}
          idLogisticaEnvio={vincularEnvioId}
          productId={vincularProductId}
          anchorCodPros={vincularAnchorCodPros}
          excludedIds={vincularExcludedIds}
          onSuccess={load}
        />
        <LogisticaEnvioEditDialog
          open={Boolean(editEnvio)}
          envio={editEnvio}
          onClose={() => {
            setEditEnvio(null);
            load();
          }}
          onSaved={load}
          dbSource="STAGE_DESIGN"
        />
        <Dialog
          open={Boolean(unlinkDialog)}
          onClose={() => {
            if (!unlinkLoading) setUnlinkDialog(null);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Quitar pedido del envío</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Se quitará el vínculo entre el envío #
              {unlinkDialog?.idEnvio ?? ""} y el pedido{" "}
              {unlinkDialog?.label ?? `#${unlinkDialog?.idPedido ?? ""}`}. El
              envío y el pedido seguirán existiendo.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setUnlinkDialog(null)}
              disabled={unlinkLoading}
            >
              Cancelar
            </Button>
            <Button
              color="warning"
              variant="contained"
              onClick={handleUnlink}
              disabled={unlinkLoading}
            >
              {unlinkLoading ? "Quitando..." : "Quitar vínculo"}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={Boolean(deleteEnvioRow)}
          onClose={() => {
            if (!deleteEnvioLoading) setDeleteEnvioRow(null);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Eliminar envío</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Se eliminará el envío #{deleteEnvioRow?.IdLogisticaEnvio ?? ""}{" "}
              con sus líneas y vínculos de pedidos. Esta acción no se puede
              deshacer.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setDeleteEnvioRow(null)}
              disabled={deleteEnvioLoading}
            >
              Cancelar
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDeleteEnvio}
              disabled={deleteEnvioLoading}
            >
              {deleteEnvioLoading ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            },
          }}
        >
          <DialogTitle
            sx={{
              borderBottom: "1px solid",
              borderColor: "divider",
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.08)} 100%)`,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.28),
                }}
              >
                <ChecklistOutlinedIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Modo de uso
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Flujo de trabajo para consolidar y revisar envíos.
                </Typography>
              </Box>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={1.2}>
              <Alert severity="info">{t("logisticaPedidos.intro")}</Alert>
              <Alert severity="warning">
                {t("logisticaPedidos.ruleSameProduct")}
              </Alert>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.4,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.9),
                }}
              >
                <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                  {t("logisticaPedidos.howTitle")}
                </Typography>
                <Box
                  component="ol"
                  sx={{ m: 0, pl: 2.25, "& li": { mb: 0.45 } }}
                >
                  <Typography component="li" variant="body2">
                    {t("logisticaPedidos.howCreate")}
                  </Typography>
                  <Typography component="li" variant="body2">
                    {t("logisticaPedidos.how1")}
                  </Typography>
                  <Typography component="li" variant="body2">
                    {t("logisticaPedidos.how2b")}
                  </Typography>
                  <Typography component="li" variant="body2">
                    {t("logisticaPedidos.how3")}
                  </Typography>
                </Box>
              </Paper>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 2.5, pb: 2 }}>
            <Button variant="contained" onClick={() => setHelpOpen(false)}>
              Entendido
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default GestionLogisticaPedidos;
